import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../lib/supabase';
import { getCookie, setCookie } from '../utils/calculatorUtils';
import '../styles/Auth.css';

const Auth = ({ initialTab = 'login' }) => {
  // Check for initialAuthTab cookie that might have been set by other components
  const [activeTab, setActiveTab] = useState(initialTab);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  
  const { signIn, signUp, signInWithGoogle } = useAuth();
  
  // Check for stored Google auth errors when component mounts
  useEffect(() => {
    setMessage({ text: '', type: '' });
    setShowSignUpPrompt(false);
    
    const googleAuthError = localStorage.getItem('googleAuthError');
    if (googleAuthError) {
      try {
        const errorData = JSON.parse(googleAuthError);
        setMessage({ 
          text: `Google login failed: ${errorData.message}`, 
          type: 'error' 
        });
        localStorage.removeItem('googleAuthError');
      } catch (e) {
        console.error('Error parsing Google auth error:', e);
        localStorage.removeItem('googleAuthError');
      }
    }
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMessage({ text: '', type: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });
    setShowSignUpPrompt(false);

    try {
      if (activeTab === 'login') {
        const { error } = await signIn(email, password);
        if (error) {
          // Check if the error is about user not found
          if (error.message.includes('user') && error.message.includes('not found')) {
            setShowSignUpPrompt(true);
            throw new Error('Account not found. Would you like to sign up?');
          }
          throw error;
        }
      } else {
        const { error } = await signUp(email, password, fullName);
        if (error) throw error;
        setMessage({ 
          text: 'Registration successful! Please check your email to confirm your account.', 
          type: 'success' 
        });
      }
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle switching to signup with email prefilled
  const handleSwitchToSignUp = () => {
    setActiveTab('signup');
    setShowSignUpPrompt(false);
    setMessage({ text: '', type: '' });
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });
    
    try {
      // Use window.location.origin for the redirect URL
      const redirectBaseUrl = window.location.origin;
      
      // Store the appropriate URL to return to after auth
      localStorage.setItem('authRedirectUrl', redirectBaseUrl);
      
      localStorage.setItem('googleAuthInProgress', 'true');
      
      setMessage({ text: 'Redirecting to Google login...', type: 'info' });
      
      // Call Supabase OAuth directly without going through the auth context
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${redirectBaseUrl}`,
          scopes: 'email profile',
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
            // Use login_hint if email is available to pre-fill the Google login form
            login_hint: email || '',
          }
        },
      });
      
      if (error) throw error;
      // If successful, the page will redirect to Google
    } catch (error) {
      setMessage({ text: `Google login failed: ${error.message}`, type: 'error' });
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <a href="/" className="back-link">← Back to Basic Calculator</a>
          <h2>The Icing Calculator</h2>
        </div>
        
        <div className="auth-tabs">
          <button 
            className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => handleTabChange('login')}
          >
            Login
          </button>
          <button 
            className={`auth-tab ${activeTab === 'signup' ? 'active' : ''}`}
            onClick={() => handleTabChange('signup')}
          >
            Sign Up
          </button>
        </div>
        
        {/* Google Sign In Button - Moved to top for better visibility */}
        <button 
          onClick={handleGoogleSignIn} 
          className="auth-button google"
          disabled={loading}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
            <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
              <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"/>
              <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"/>
              <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"/>
              <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"/>
            </g>
          </svg>
          Continue with Google
        </button>
        
        <div className="auth-divider">
          <span>OR</span>
        </div>
        
        {message.text && (
          <div className={`auth-message ${message.type}`}>
            {message.text}
          </div>
        )}
        
        {/* Sign Up Prompt */}
        {showSignUpPrompt && (
          <div className="signup-prompt">
            <p>Would you like to create a new account with this email?</p>
            <button 
              onClick={handleSwitchToSignUp}
              className="auth-button secondary"
            >
              Create Account
            </button>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="auth-form">
          {activeTab === 'signup' && (
            <div className="form-group">
              <label htmlFor="fullName">Full Name</label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required={activeTab === 'signup'}
                disabled={loading}
              />
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              minLength={6}
            />
          </div>
          
          <button 
            type="submit" 
            className="auth-button primary"
            disabled={loading}
          >
            {loading ? 'Processing...' : activeTab === 'login' ? 'Login' : 'Sign Up'}
          </button>
        </form>
        

      </div>
    </div>
  );
};

export default Auth;
